import { useCallback, useEffect, useRef } from 'react';
import { normalizeOtp } from '../utils/otp';

export default function useSmsOtp(onCode) {
  const callback = useRef(onCode);
  const current = useRef(null);
  useEffect(() => { callback.current = onCode; }, [onCode]);

  const cancel = useCallback(() => {
    if (!current.current) return;
    clearTimeout(current.current.timer);
    current.current.controller.abort();
    current.current = null;
  }, []);

  const start = useCallback(() => {
    cancel();
    if (!window.isSecureContext || !('OTPCredential' in window) || !navigator.credentials?.get) return;
    const controller = new AbortController();
    const request = { controller, timer: setTimeout(cancel, 60000) };
    current.current = request;
    try {
      // Called synchronously by the send/resend handler BEFORE the SMS HTTP request.
      Promise.resolve(navigator.credentials.get({ otp: { transport: ['sms'] }, signal: controller.signal }))
        .then(credential => {
          if (controller.signal.aborted || current.current !== request) return;
          const code = normalizeOtp(credential?.code);
          if (code.length === 5) callback.current(code);
        })
        .catch(() => { /* Manual input and iOS keyboard suggestions remain available. */ })
        .finally(() => {
          clearTimeout(request.timer);
          if (current.current === request) current.current = null;
        });
    } catch { cancel(); }
  }, [cancel]);

  useEffect(() => cancel, [cancel]);
  return { start, cancel };
}
